import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useAnimationControls } from "framer-motion";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import { useCritterSoundManager } from "../components/games/critter-crossing/SoundManager";
import RiverScene from "../components/games/critter-crossing/RiverScene";
import ChallengeLayer from "../components/games/critter-crossing/Challenges";
import {
  CRITTERS,
  HOP_STYLES,
  LAYOUT,
  MATH_SKILLS,
  NARRATOR_INTRO,
  NARRATOR_VOICE,
  PLAIN_STONE,
  STONE_COLORS,
  advanceLevel,
  celebrationLine,
  challengeSpokenLines,
  levelName,
  loadLevel,
  makeRound,
  onLandStyle,
  pathSlots,
  randomMood,
  saveLevel,
  scoreForMistakes,
  skillsInRound,
} from "../components/games/critter-crossing/gameUtils";
import BackToGamesButton from "../components/games/BackToGamesButton";
import { SpeakerIcon, StarIcon, SproutIcon, PawIcon } from "../components/games/ui-icons";
import "./CritterCrossingPage.css";

const F = { fontFamily: "'Fredoka', sans-serif" };
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const sideFor = (dims) => Math.min(dims.w || 0, dims.h || 0) * 0.24;

// Preload every sprite a round can show so nothing pops in mid-hop.
const preloadAssets = () => {
  const urls = [PLAIN_STONE];
  CRITTERS.forEach((critter) => urls.push(critter.waitingAsset, critter.happyAsset));
  Object.values(STONE_COLORS).forEach((color) => urls.push(color.asset));
  urls.forEach((url) => {
    const image = new window.Image();
    image.src = url;
  });
};

const StartModal = ({ onStart, onReplay }) => (
  <motion.div
    className="cc-modal-backdrop"
    role="dialog"
    aria-modal="true"
    aria-labelledby="cc-start-title"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <motion.div
      className="cc-modal"
      initial={{ scale: 0.85, y: 20 }}
      animate={{ scale: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 240, damping: 20 }}
    >
      <div className="cc-modal-emoji" aria-hidden="true">
        <img
          src="/assets/games/critter-crossing/cc_turtle_happy.png"
          alt=""
          className="mx-auto h-16 w-16 object-contain"
          draggable={false}
        />
      </div>
      <h2 id="cc-start-title">Critter Crossing</h2>
      <p>{NARRATOR_INTRO}</p>
      <div className="cc-how-list">
        <div className="cc-how-step"><span className="cc-how-num">1</span><span>A woodland friend hops up and asks to cross.</span></div>
        <div className="cc-how-step"><span className="cc-how-num">2</span><span>Solve a little counting puzzle on the water.</span></div>
        <div className="cc-how-step"><span className="cc-how-num">3</span><span>Watch the stones rise and hop them across!</span></div>
      </div>
      <div className="cc-modal-actions">
        <button type="button" className="cc-secondary-btn" onClick={onReplay} aria-label="Hear the intro"><SpeakerIcon size={18} /></button>
        <button type="button" className="cc-primary-btn" onClick={onStart}>Let&apos;s help!</button>
      </div>
    </motion.div>
  </motion.div>
);


const ResultStar = ({ delay, dim = false }) => (
  <motion.svg
    className={`cc-result-star ${dim ? "is-dim" : ""}`}
    viewBox="0 0 24 24"
    aria-hidden="true"
    initial={{ scale: 0, rotate: -35 }}
    animate={{ scale: dim ? 0.86 : 1, rotate: 0 }}
    transition={{ type: "spring", stiffness: 300, damping: 11, delay }}
  >
    <path
      d="M12 2.4l2.83 6.02 6.62.72-4.95 4.45 1.36 6.5L12 17.9l-5.86 4.19 1.36-6.5L2.55 9.14l6.62-.72z"
      fill={dim ? "#E8E3D8" : "#FFC93C"}
      stroke={dim ? "#D4CEC0" : "#F09E1A"}
      strokeWidth="1.1"
      strokeLinejoin="round"
    />
  </motion.svg>
);

// Friendly skill names for the "skills practiced" strip — the in-game
// "Counting Power" style labels read as badges, not as what was learned.
const SKILL_NAMES = {
  counting: "Counting",
  adding: "Adding",
  comparing: "Comparing",
  patterns: "Patterns",
  numerals: "Numbers",
  quantity: "How many",
};

const ResultOverlay = ({ tasks, mistakes, starsAwarded, levelChange, nextLevel, onPlayAgain, onDone }) => {
  const skills = skillsInRound(tasks);
  const critters = tasks.slice(0, 5);
  const starCount = mistakes === 0 ? 3 : 2;
  const levelMessage =
    levelChange > 0
      ? `Leveling up to ${levelName(nextLevel)}!`
      : levelChange < 0
        ? `Easing to ${levelName(nextLevel)} — you've got this!`
        : null;
  return (
    <motion.div className="cc-result-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <motion.div
        className="cc-result"
        initial={{ scale: 0.8, y: 24 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 220, damping: 20 }}
      >
        <div className="cc-result-parade" aria-hidden="true">
          {critters.map((task, index) => (
            <motion.img
              key={task.id ?? index}
              className="cc-result-critter"
              src={task.critter.happyAsset}
              alt=""
              initial={{ y: 26, opacity: 0, scale: 0.5 }}
              animate={{ y: [0, -9, 0], opacity: 1, scale: 1 }}
              transition={{
                opacity: { delay: 0.12 + index * 0.08, duration: 0.3 },
                scale: { type: "spring", stiffness: 300, damping: 13, delay: 0.12 + index * 0.08 },
                y: { repeat: Infinity, duration: 1.5, ease: "easeInOut", delay: 0.45 + index * 0.12 },
              }}
            />
          ))}
        </div>

        {/* Performance rating on an explicit 3-star scale — the missed star
            shows greyed so "2 of 3" reads as a score, not a random count. */}
        <div className="cc-result-starrow" aria-label={`${starCount} of 3 stars`}>
          {[0, 1, 2].map((index) => (
            <ResultStar key={index} delay={0.28 + index * 0.13} dim={index >= starCount} />
          ))}
        </div>

        <h2>Everyone made it across!</h2>
        <p>
          {mistakes === 0
            ? `You helped all ${tasks.length} critters with super counting — not a single slip!`
            : `You helped all ${tasks.length} critters across the river!`}
        </p>

        {starsAwarded > 0 && <div className="cc-result-reward">+{starsAwarded} stars earned</div>}

        {levelMessage && (
          <motion.div
            className={`cc-result-level ${levelChange > 0 ? "is-up" : "is-down"}`}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 16, delay: 0.4 }}
          >
            <span aria-hidden="true">{levelChange > 0 ? <StarIcon size={15} /> : <SproutIcon size={15} />}</span>
            {levelMessage}
          </motion.div>
        )}

        <div className="cc-result-skills">
          <span className="cc-skills-head">Skills practiced</span>
          <div className="cc-skills-row">
            {skills.map((skill, index) => (
              <motion.span
                className="cc-skill-chip"
                key={skill}
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 18, delay: 0.45 + index * 0.07 }}
              >
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden>
                  <path d="m2.5 7.5 3 3 6-7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {SKILL_NAMES[skill] || MATH_SKILLS[skill].label}
              </motion.span>
            ))}
          </div>
        </div>

        <div className="cc-result-actions">
          <button type="button" className="cc-secondary-btn wide" onClick={onDone}>Done</button>
          <button type="button" className="cc-primary-btn" onClick={onPlayAgain}>Play again</button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const CONFETTI = Array.from({ length: 26 }, (_, index) => ({
  id: index,
  left: `${(index * 37) % 100}%`,
  delay: (index % 7) * 0.08,
  color: ["#ff8a5c", "#ffd23f", "#6fd08c", "#4db6f7", "#c084fc", "#ff6b9d"][index % 6],
}));

export default function CritterCrossingPage() {
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const { refreshUserData } = useAuth();
  const sounds = useCritterSoundManager();

  // Adaptive difficulty (1 gentle → 3 challenging), cached locally so it
  // follows the child across sessions. Read once up front.
  const levelRef = useRef(loadLevel());
  const ratingsRef = useRef([]);

  const [started, setStarted] = useState(false);
  const [phase, setPhase] = useState("transition"); // transition | challenge | celebrate
  const [tasks, setTasks] = useState(() => makeRound(levelRef.current));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [mood, setMood] = useState(() => randomMood());
  // How the level moved after this round (+1 up, -1 down) and the level it
  // moved to — shown on the result overlay.
  const [levelChange, setLevelChange] = useState(0);

  const [stones, setStones] = useState([]);
  const [stoneBounce, setStoneBounce] = useState({});
  const [friends, setFriends] = useState([]);
  const [cheerTick, setCheerTick] = useState(0);
  const [sparkleTick, setSparkleTick] = useState(0);
  const [splash, setSplash] = useState(null);
  const [celebrating, setCelebrating] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [starsAwarded, setStarsAwarded] = useState(0);
  const [actor, setActor] = useState({ critter: null, happy: false, bubble: null, bubbleUnit: LAYOUT.waitingSpot });
  const [celebLine] = useState(() => celebrationLine());

  const wrapperControls = useAnimationControls();
  const bodyControls = useAnimationControls();
  const shadowControls = useAnimationControls();

  const stageRef = useRef(null);
  const dimsRef = useRef({ w: 0, h: 0 });
  const tasksRef = useRef(tasks);
  const stonesRef = useRef([]);
  const helpedRef = useRef([]);
  const indexRef = useRef(0);
  const mistakesRef = useRef(0);
  const currentUnitRef = useRef(LAYOUT.entrySpot);
  const stoneSeq = useRef(0);
  const splashSeq = useRef(0);
  const savedRef = useRef(false);
  const runningRef = useRef(false);

  // Measure the stage so the hop choreography can work in pixels.
  useEffect(() => {
    const node = stageRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      dimsRef.current = { w: rect.width, h: rect.height };
      setDims({ w: rect.width, h: rect.height });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => () => sounds.stop(), [sounds]);

  // Warm this round's voices up front so lines play in the real ElevenLabs
  // voice on first use (mirrors the iOS per-round prefetch).
  useEffect(() => {
    prefetchRoundVoices(tasksRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Stage mutators ----
  const placeStone = useCallback((unit, color = null) => {
    const id = `stone-${stoneSeq.current}`;
    stoneSeq.current += 1;
    const stone = { id, unit, color };
    stonesRef.current = [...stonesRef.current, stone];
    setStones(stonesRef.current);
    return id;
  }, []);

  const clearStones = useCallback(() => {
    stonesRef.current = [];
    setStones([]);
    setStoneBounce({});
  }, []);

  const bounceStone = useCallback((id) => {
    setStoneBounce((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
  }, []);

  const triggerSplash = useCallback(
    (unit) => {
      splashSeq.current += 1;
      setSplash({ unit, id: splashSeq.current });
      sounds.playSplash();
    },
    [sounds]
  );

  const registerMistake = useCallback(() => {
    mistakesRef.current += 1;
  }, []);

  const positionAt = useCallback(
    (unit) => {
      const side = sideFor(dimsRef.current);
      wrapperControls.set({ x: unit.x * dimsRef.current.w - side / 2, y: unit.y * dimsRef.current.h - side });
      bodyControls.set({ scaleX: 1, scaleY: 1, rotate: 0 });
      shadowControls.set({ scaleX: 1, opacity: 0.18 });
      currentUnitRef.current = unit;
    },
    [wrapperControls, bodyControls, shadowControls]
  );

  // One full squash-and-stretch hop in the critter's own gait.
  const hop = useCallback(
    async (landing, index, total, style, onWater) => {
      const { w: W, h: H } = dimsRef.current;
      const side = sideFor(dimsRef.current);
      const start = currentUnitRef.current;
      const dir = landing.x >= start.x ? 1 : -1;
      const s = style.stretch;
      const apex = { x: (start.x + landing.x) / 2, y: Math.min(start.y, landing.y) - style.apexLift };
      const at = (unit) => ({ x: unit.x * W - side / 2, y: unit.y * H - side });

      // Anticipation: crouch.
      await Promise.all([
        bodyControls.start({ scaleX: 1 + 0.09 * s, scaleY: 1 - 0.13 * s, transition: { duration: style.crouch, ease: "easeIn" } }),
        shadowControls.start({ scaleX: 0.78 / 0.7, opacity: 0.22, transition: { duration: style.crouch } }),
      ]);
      // Launch: stretch up toward the apex, leaning in.
      await Promise.all([
        wrapperControls.start({ ...at(apex), transition: { duration: style.rise, ease: "easeOut" } }),
        bodyControls.start({ scaleX: 1 - 0.07 * s, scaleY: 1 + 0.14 * s, rotate: style.lean * dir, transition: { duration: style.rise, ease: "easeOut" } }),
        shadowControls.start({ scaleX: 0.44 / 0.7, opacity: 0.08, transition: { duration: style.rise } }),
      ]);
      // Fall: drop onto the landing, bracing slightly back.
      await Promise.all([
        wrapperControls.start({ ...at(landing), transition: { duration: style.fall, ease: "easeIn" } }),
        bodyControls.start({ rotate: -style.lean * 0.45 * dir, transition: { duration: style.fall, ease: "easeIn" } }),
      ]);
      currentUnitRef.current = landing;
      // Touchdown: squash, splash, wobble the stone underfoot.
      if (onWater) {
        triggerSplash(landing);
        const under = stonesRef.current.find((stone) => stone.unit.x === landing.x && stone.unit.y === landing.y);
        if (under) bounceStone(under.id);
        if (style.heavy) sounds.playThud();
      }
      sounds.playPop(onWater ? (index + 1) / Math.max(total, 1) : 0.25);
      await Promise.all([
        bodyControls.start({ scaleX: 1 + 0.13 * s, scaleY: 1 - 0.18 * s, rotate: 0, transition: { duration: 0.09, ease: "easeOut" } }),
        shadowControls.start({ scaleX: 0.82 / 0.7, opacity: 0.24, transition: { duration: 0.09 } }),
      ]);
      // Recover.
      await Promise.all([
        bodyControls.start({ scaleX: 1, scaleY: 1, transition: { type: "spring", stiffness: 480, damping: 16 } }),
        shadowControls.start({ scaleX: 1, opacity: 0.18, transition: { duration: 0.2 } }),
      ]);
      await sleep(style.rest * 1000);
    },
    [bodyControls, bounceStone, shadowControls, sounds, triggerSplash, wrapperControls]
  );

  // Warms the TTS cache for everything this round can say, in play order: the
  // intro, then each critter's lines + their challenge's lines, then the cheer.
  const prefetchRoundVoices = useCallback(
    (round) => {
      const lines = [{ text: NARRATOR_INTRO, voice: NARRATOR_VOICE }];
      round.forEach((task) => {
        lines.push({ text: task.introLine, voice: task.critter.voice });
        challengeSpokenLines(task.challenge).forEach((text) =>
          lines.push({ text, voice: NARRATOR_VOICE })
        );
        lines.push({ text: task.thanksLine, voice: task.critter.voice });
      });
      lines.push({ text: celebLine, voice: NARRATOR_VOICE });
      sounds.warm(lines);
    },
    [celebLine, sounds]
  );

  const persistProgress = useCallback(async () => {
    if (savedRef.current) return;
    savedRef.current = true;

    // Nudge the adaptive level for NEXT round (two clean rounds level up, two
    // rough ones level down) and remember which way it moved for the overlay.
    const previousLevel = levelRef.current;
    const advanced = advanceLevel(previousLevel, ratingsRef.current, mistakesRef.current);
    ratingsRef.current = advanced.ratings;
    levelRef.current = advanced.level;
    saveLevel(advanced.level);
    setLevelChange(advanced.level - previousLevel);

    if (!apiClient) return;
    const score = scoreForMistakes(mistakesRef.current);
    try {
      const response = await apiClient.postGameProgress("CRITTER_CROSSING", score, true, null);
      setStarsAwarded(Number(response?.stars_awarded || 0));
      window.dispatchEvent(new CustomEvent("games:progress-updated"));
      refreshUserData?.();
    } catch {
      // Saving progress must never interrupt a child's celebration.
    }
  }, [apiClient, refreshUserData]);

  const startCelebration = useCallback(async () => {
    setPhase("celebrate");
    setCelebrating(true);
    sounds.playCelebrate();
    sounds.speak(celebLine);
    persistProgress();
    await sleep(1000);
    setShowResult(true);
  }, [celebLine, persistProgress, sounds]);

  const enterCurrentCritter = useCallback(async () => {
    const task = tasksRef.current[indexRef.current];
    if (!task) return;
    setActor({ critter: task.critter, happy: false, bubble: null, bubbleUnit: LAYOUT.waitingSpot });
    currentUnitRef.current = LAYOUT.entrySpot;
    await sleep(80);
    positionAt(LAYOUT.entrySpot);
    await sleep(260);

    const style = onLandStyle(HOP_STYLES[task.critter.hopKey]);
    const entry = LAYOUT.entrySpot;
    const waiting = LAYOUT.waitingSpot;
    for (let step = 1; step <= 3; step += 1) {
      const t = step / 3;
      const point = { x: entry.x + (waiting.x - entry.x) * t, y: entry.y + (waiting.y - entry.y) * t };
      // eslint-disable-next-line no-await-in-loop
      await hop(point, step, 3, style, false);
    }

    setActor((a) => ({ ...a, bubble: task.introLine, bubbleUnit: LAYOUT.waitingSpot }));
    // Hold until the whole line has been SPOKEN (with a minimum readable
    // beat) — the old fixed 2.6s sleep cut long intros off mid-sentence when
    // the challenge prompt's own narration cancelled them.
    await Promise.all([sounds.speak(task.introLine, task.critter.voice), sleep(2000)]);
    await sleep(300);
    setActor((a) => ({ ...a, bubble: null }));
    setPhase("challenge");
  }, [hop, positionAt, sounds]);

  const runCrossing = useCallback(async () => {
    const task = tasksRef.current[indexRef.current];
    if (!task) return;
    const style = HOP_STYLES[task.critter.hopKey];

    // Some challenges don't build their own stones — raise the reward path
    // here, one stone per counted friend, so the answer becomes the bridge.
    if (stonesRef.current.length === 0) {
      const stoneCount = task.challenge.rewardStoneCount ?? 5;
      const slots = pathSlots(stoneCount);
      for (let i = 0; i < slots.length; i += 1) {
        placeStone(slots[i]);
        triggerSplash(slots[i]);
        sounds.playPop((i + 1) / (stoneCount + 1));
        // eslint-disable-next-line no-await-in-loop
        await sleep(180);
      }
      await sleep(400);
    }
    await sleep(300);

    const landings = [...stonesRef.current]
      .sort((a, b) => a.unit.x - b.unit.x)
      .map((stone) => stone.unit)
      .concat([LAYOUT.arrivalSpot]);
    for (let i = 0; i < landings.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await hop(landings[i], i, landings.length, style, i < landings.length - 1);
    }

    setActor((a) => ({ ...a, happy: true }));
    setSparkleTick((t) => t + 1);
    setCheerTick((t) => t + 1);
    sounds.playCelebrate();
    sounds.speak(task.thanksLine, task.critter.voice);
    await sleep(1600);

    const friendSpot = LAYOUT.friendSpots[helpedRef.current.length % LAYOUT.friendSpots.length];
    await hop(friendSpot, 0, 1, onLandStyle(style), false);
    helpedRef.current = [...helpedRef.current, task.critter];
    setFriends(helpedRef.current);
    setActor({ critter: null, happy: false, bubble: null, bubbleUnit: LAYOUT.waitingSpot });
    clearStones();

    indexRef.current += 1;
    setCurrentIndex(indexRef.current);
    await sleep(500);

    if (indexRef.current >= tasksRef.current.length) {
      await startCelebration();
    } else {
      await enterCurrentCritter();
    }
  }, [clearStones, enterCurrentCritter, hop, placeStone, sounds, startCelebration, triggerSplash]);

  const challengeSolved = useCallback(() => {
    if (runningRef.current) return;
    runningRef.current = true;
    setPhase("transition");
    runCrossing().finally(() => {
      runningRef.current = false;
    });
  }, [runCrossing]);

  const begin = useCallback(() => {
    setStarted(true);
    sounds.playPop(0.6);
    setPhase("transition");
    enterCurrentCritter();
  }, [enterCurrentCritter, sounds]);

  const playAgain = useCallback(() => {
    const round = makeRound(levelRef.current);
    tasksRef.current = round;
    setTasks(round);
    setMood(randomMood());
    indexRef.current = 0;
    setCurrentIndex(0);
    mistakesRef.current = 0;
    helpedRef.current = [];
    setFriends([]);
    clearStones();
    setSplash(null);
    setSparkleTick(0);
    setCheerTick(0);
    setCelebrating(false);
    setShowResult(false);
    setStarsAwarded(0);
    setLevelChange(0);
    savedRef.current = false;
    setActor({ critter: null, happy: false, bubble: null, bubbleUnit: LAYOUT.waitingSpot });
    prefetchRoundVoices(round);
    setPhase("transition");
    enterCurrentCritter();
  }, [clearStones, enterCurrentCritter, prefetchRoundVoices]);

  const handleStart = useCallback(() => {
    preloadAssets();
    begin();
  }, [begin]);

  const handleExit = useCallback(() => {
    sounds.stop();
    navigate("/games");
  }, [navigate, sounds]);

  const currentTask = tasks[currentIndex];
  const actorState = useMemo(
    () => ({ ...actor, wrapperControls, bodyControls, shadowControls }),
    [actor, wrapperControls, bodyControls, shadowControls]
  );

  const actions = useMemo(
    () => ({ placeStone, splashAt: triggerSplash, bounceStone, onMistake: registerMistake, onSolved: challengeSolved }),
    [placeStone, triggerSplash, bounceStone, registerMistake, challengeSolved]
  );

  const subtitle = started
    ? phase === "celebrate"
      ? "Every critter made it!"
      : actor.critter?.name || "Help the critters cross"
    : "Help the critters cross the river";

  return (
    <main className="cc-page" style={F}>
      <div className="cc-shell">
        <header className="cc-topbar">
          <BackToGamesButton onClick={handleExit} accent="#c94a80" />
          <div className="cc-title-wrap">
            <h1>Critter Crossing</h1>
            <p>{subtitle}</p>
          </div>
          <div className="cc-topbar-right">
            {started && (
              <div className="cc-crossings-chip" aria-label={`${friends.length} of ${tasks.length} critters across`}>
                <span className="cc-chip-num">{friends.length}</span>
                <span className="cc-chip-sep">/</span>
                <span>{tasks.length}</span>
              </div>
            )}
            <button
              type="button"
              className="cc-icon-btn cc-icon-btn--square"
              onClick={sounds.toggleMute}
              aria-label={sounds.muted ? "Turn sound on" : "Mute sounds"}
              aria-pressed={sounds.muted}
            >
              <span aria-hidden="true"><SpeakerIcon size={20} muted={sounds.muted} /></span>
            </button>
          </div>
        </header>

        {started && (
          <div className="cc-meter" aria-label={`${friends.length} of ${tasks.length} critters across`}>
            <span className="cc-meter-paw" aria-hidden="true"><PawIcon size={18} /></span>
            <div className="cc-meter-track">
              <motion.div
                className="cc-meter-fill"
                animate={{ width: `${(friends.length / tasks.length) * 100}%` }}
                transition={{ type: "spring", stiffness: 120, damping: 18 }}
              />
            </div>
            <span className="cc-meter-count">{friends.length}/{tasks.length}</span>
          </div>
        )}

        <div className="cc-stage" ref={stageRef}>
          <RiverScene
            dims={dims}
            stones={stones}
            stoneBounce={stoneBounce}
            friends={friends}
            cheerTick={cheerTick}
            splash={splash}
            sparkleTick={sparkleTick}
            actor={actorState}
            celebrating={celebrating}
            mood={mood}
          />

          <AnimatePresence>
            {started && phase === "challenge" && currentTask && (
              <ChallengeLayer
                key={currentTask.id}
                task={currentTask}
                dims={dims}
                sounds={sounds}
                actions={actions}
              />
            )}
          </AnimatePresence>

          <AnimatePresence>
            {celebrating && (
              <motion.div className="cc-confetti" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {CONFETTI.map((bit) => (
                  <motion.i
                    key={bit.id}
                    style={{ left: bit.left, background: bit.color }}
                    initial={{ y: -40, opacity: 0, rotate: 0 }}
                    animate={{ y: "115%", opacity: [0, 1, 1, 0], rotate: 540 }}
                    transition={{ duration: 2.6, delay: bit.delay, repeat: Infinity, ease: "easeIn" }}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {!started && <StartModal onStart={handleStart} onReplay={() => sounds.speak(NARRATOR_INTRO)} />}
      </AnimatePresence>

      <AnimatePresence>
        {showResult && (
          <ResultOverlay
            tasks={tasks}
            mistakes={mistakesRef.current}
            starsAwarded={starsAwarded}
            levelChange={levelChange}
            nextLevel={levelRef.current}
            onPlayAgain={playAgain}
            onDone={handleExit}
          />
        )}
      </AnimatePresence>
    </main>
  );
}
